import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { API, useAuth } from '@/App';

// Simple Math CAPTCHA Component
const MathCaptcha = ({ onVerify }) => {
  const [num1] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [num2] = useState(() => Math.floor(Math.random() * 10) + 1);
  const [answer, setAnswer] = useState('');
  const [verified, setVerified] = useState(false);

  const handleVerify = () => {
    if (parseInt(answer) === num1 + num2) {
      setVerified(true);
      onVerify(true);
      toast.success('Verification successful!');
    } else {
      toast.error('Incorrect answer. Please try again.');
      setAnswer('');
    }
  };

  if (verified) {
    return (
      <div className="flex items-center gap-2 p-3 bg-risk-low-bg border border-risk-low-border rounded-sm">
        <CheckCircle size={20} className="text-risk-low-text" weight="fill" />
        <span className="text-sm text-risk-low-text font-medium">Verified as human</span>
      </div>
    );
  }

  return (
    <div className="p-4 bg-background-surface border border-border rounded-sm">
      <div className="text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-3">
        Prove you are human
      </div>
      <div className="flex items-center gap-3">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-lg font-mono text-gold font-semibold">{num1}</span>
          <span className="text-text-secondary">+</span>
          <span className="text-lg font-mono text-gold font-semibold">{num2}</span>
          <span className="text-text-secondary">=</span>
          <input
            type="number"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
            className="w-16 px-3 py-2 bg-background border border-border rounded-sm text-text-primary text-center font-mono focus:border-gold focus:outline-none"
            placeholder="?"
            data-testid="captcha-answer-input"
          />
        </div>
        <button
          type="button"
          onClick={handleVerify}
          className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground text-sm rounded-sm transition-all"
          data-testid="captcha-verify-btn"
        >
          Verify
        </button>
      </div>
    </div>
  );
};

// Forgot Password Modal
const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: email, 2: captcha, 3: new password, 4: success
  const [email, setEmail] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      setResetToken(response.data.reset_token);
      toast.success('Email verified. Please complete verification.');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email not found');
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaVerify = (verified) => {
    if (verified) {
      setCaptchaVerified(true);
      setTimeout(() => setStep(3), 500);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        reset_token: resetToken,
        new_password: newPassword
      });
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setEmail('');
    setCaptchaVerified(false);
    setNewPassword('');
    setConfirmPassword('');
    setResetToken('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background-surface border border-border rounded-sm w-full max-w-md mx-4"
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center gap-3">
              {step > 1 && step < 4 && (
                <button 
                  onClick={() => setStep(step - 1)} 
                  className="text-text-tertiary hover:text-text-primary"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <h2 className="text-lg font-heading font-medium text-text-primary">
                {step === 1 && 'Reset Password'}
                {step === 2 && 'Verify Identity'}
                {step === 3 && 'Create New Password'}
                {step === 4 && 'Password Reset'}
              </h2>
            </div>
            <button onClick={handleClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>

          <div className="p-6">
            {/* Step 1: Enter Email */}
            {step === 1 && (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <p className="text-sm text-text-secondary mb-4">
                  Enter your email address and we'll help you reset your password.
                </p>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                    placeholder="you@company.com"
                    data-testid="forgot-email-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Verifying...' : 'Continue'}
                </button>
              </form>
            )}

            {/* Step 2: CAPTCHA Verification */}
            {step === 2 && (
              <div className="space-y-4">
                <p className="text-sm text-text-secondary mb-4">
                  Complete this verification to prove you're not a robot.
                </p>
                <MathCaptcha onVerify={handleCaptchaVerify} />
              </div>
            )}

            {/* Step 3: New Password */}
            {step === 3 && (
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <p className="text-sm text-text-secondary mb-4">
                  Create a new secure password for your account.
                </p>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                    placeholder="••••••••"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-text-tertiary font-semibold mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                    placeholder="••••••••"
                    data-testid="confirm-password-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-4 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50"
                  data-testid="reset-password-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            )}

            {/* Step 4: Success */}
            {step === 4 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-risk-low-bg border border-risk-low-border rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-risk-low-text" weight="fill" />
                </div>
                <h3 className="text-lg font-heading font-medium text-text-primary mb-2">
                  Password Reset Successful
                </h3>
                <p className="text-sm text-text-secondary mb-6">
                  Your password has been updated. You can now sign in with your new password.
                </p>
                <button
                  onClick={handleClose}
                  className="px-6 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all"
                  data-testid="back-to-login-btn"
                >
                  Back to Sign In
                </button>
              </div>
            )}
          </div>

          {/* Progress Indicator */}
          {step < 4 && (
            <div className="px-6 pb-6">
              <div className="flex gap-2">
                {[1, 2, 3].map((s) => (
                  <div
                    key={s}
                    className={`flex-1 h-1 rounded-full transition-all ${
                      s <= step ? 'bg-gold' : 'bg-border'
                    }`}
                  />
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: ''
  });
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_risk-engine-pro-2/artifacts/2vz12eng_Asset%203.png" 
              alt="Navigate Logo" 
              className="h-12 w-12 mb-4"
            />
            <h1 className="text-3xl font-heading font-medium text-text-primary mb-2">
              {isLogin ? 'Welcome Back' : 'Get Started'}
            </h1>
            <p className="text-text-secondary">
              {isLogin ? 'Access your compliance dashboard' : 'Create your compliance account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-text-secondary font-semibold mb-2">
                  Company Name
                </label>
                <input
                  data-testid="auth-company-name-input"
                  type="text"
                  required={!isLogin}
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none transition-all"
                  placeholder="Your Company"
                />
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-text-secondary font-semibold mb-2">
                Email
              </label>
              <input
                data-testid="auth-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none transition-all"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs uppercase tracking-[0.15em] text-text-secondary font-semibold">
                  Password
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-xs text-gold hover:text-gold-hover transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <input
                data-testid="auth-password-input"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none transition-all"
                placeholder="••••••••"
              />
            </div>

            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-gold hover:bg-gold-hover text-gold-foreground font-medium rounded-sm transition-all duration-200 disabled:opacity-50"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="auth-toggle-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-text-secondary hover:text-gold transition-colors"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Visual */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-surface to-background">
          <img 
            src="https://images.unsplash.com/photo-1761437856311-3ba13025f161?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxibGFjayUyMGdvbGQlMjBsdXh1cnklMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzQyNzAwODN8MA&ixlib=rb-4.1.0&q=85" 
            alt="" 
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="text-center">
            <h2 className="text-4xl font-heading font-light text-text-primary mb-4">
              Compliance.
              <br />
              <span className="text-gold font-medium">Reinvented.</span>
            </h2>
            <p className="text-text-secondary text-lg">
              Built for South African businesses
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPassword} 
        onClose={() => setShowForgotPassword(false)} 
      />
    </div>
  );
};

export default AuthPage;
